import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@/hooks/useAuth0';
import { AppLayout } from './Layout/AppLayout';
import { ROUTES } from '@/utils/routes';
import { CurrentSessionProvider } from '../providers/CurrentSessionProvider';

const centerLoadingStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh'
};

const notFoundStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '50px'
};

const CallbackPage = lazy(() => import('@/pages/Auth/CallbackPage').then(m => ({ default: m.CallbackPage })));
const UnauthorizedPage = lazy(() => import('@/pages/Auth/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })));

const Welcome = lazy(() => import('@/pages/CommunityInput/Welcome').then(m => ({ default: m.Welcome })));
const Search = lazy(() => import('@/pages/CommunityInput/Search').then(m => ({ default: m.Search })));
const Submit = lazy(() => import('@/pages/CommunityInput/Submit').then(m => ({ default: m.Submit })));
const DownloadSalesforce = lazy(() => import('@/pages/CommunityInput/DownloadSalesforce').then(m => ({ default: m.DownloadSalesforce })));

const CharacteristicsIncome = lazy(() => import('@/pages/CommunityCharacteristics/IncomeRestricted').then(m => ({ default: m.IncomeRestricted })));
const CharacteristicsSection8 = lazy(() => import('@/pages/CommunityCharacteristics/Section8.tsx').then(m => ({ default: m.Section8 })));
const CharacteristicsNewConstruction = lazy(() => import('@/pages/CommunityCharacteristics/NewConstruction.tsx').then(m => ({ default: m.NewConstruction })));
const CharacteristicsStudent = lazy(() => import('@/pages/CommunityCharacteristics/Student.tsx').then(m => ({ default: m.Student })));
const CharacteristicsSharedOps = lazy(() => import('@/pages/CommunityCharacteristics/SharedOperations.tsx').then(m => ({ default: m.SharedOperations })));
const SubmitForReview = lazy(() => import('@/pages/CommunityCharacteristics/SubmitForReview.tsx').then(m => ({ default: m.SubmitForReview })));
const CharacteristicsWelcome = lazy(() => import('@/pages/CommunityCharacteristics/Welcome').then(m => ({ default: m.CharacteristicsWelcome })));

// Implementation Contacts
const ImplementationContactsWelcome = lazy(() => import('@/pages/ImplementationContacts/Welcome').then(m => ({ default: m.default })));
const ImplementationProjectLead = lazy(() => import('@/pages/ImplementationContacts/ContactRolePage').then(m => ({ default: m.default })));
const ImplementationResident = lazy(() => import('@/pages/ImplementationContacts/ContactRolePage').then(m => ({ default: m.default })));
const ImplementationTechnical = ImplementationResident;
const ImplementationVoice = ImplementationResident;
const ImplementationKnowledge = ImplementationResident;
const ImplementationTraining = ImplementationResident;
const ImplementationOwner = ImplementationResident;
const ImplementationMarketing = ImplementationResident;
const ImplementationContactsSubmit = lazy(() => import('@/pages/ImplementationContacts/Submit').then(m => ({ default: m.default })));

// EliseGO Configuration
const EliseGOName = lazy(() => import('@/pages/EliseGO/Name.tsx').then(m => ({ default: m.default })));
const EliseGOAIName = lazy(() => import('@/pages/EliseGO/AIName.tsx').then(m => ({ default: m.default })));
const EliseGOVoiceArea = lazy(() => import('@/pages/EliseGO/VoiceAreaCode.tsx').then(m => ({ default: m.default })));
const EliseGOScheduleTours = lazy(() => import('@/pages/EliseGO/ScheduleTours.tsx').then(m => ({ default: m.default })));
const EliseGOSubmit = lazy(() => import('@/pages/EliseGO/Submit').then(m => ({ default: m.default })));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  
  React.useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);
  
  if (isLoading) {
    return (
      <div style={centerLoadingStyle}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={centerLoadingStyle}>
        <div>Redirecting to login...</div>
      </div>
    );
  }

  return <>{children}</>;
};

const NotFound: React.FC = () => (
  <div style={notFoundStyle}>
    <h1>404 - Page Not Found</h1>
    <p>The page you are looking for does not exist.</p>
  </div>
);

const PageLoader: React.FC = () => (
  <div style={centerLoadingStyle}>
    <div>Loading...</div>
  </div>
);

const NavigateWithCurrentParams: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  React.useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        window.location.replace(`/welcome${window.location.search}${window.location.hash}`);
      } else {
        loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        });
      }
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  return (
    <div style={centerLoadingStyle}>
      <div>Redirecting...</div>
    </div>
  );
};

export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route
        path={ROUTES.COMMUNITY_INPUT.DOWNLOAD_SALESFORCE}
        element={<ProtectedRoute><DownloadSalesforce /></ProtectedRoute>}
      />
      
      <Route element={(
        <ProtectedRoute>
          <CurrentSessionProvider>
            <AppLayout />
          </CurrentSessionProvider>
        </ProtectedRoute>
      )}>
        <Route path={ROUTES.COMMUNITY_INPUT.WELCOME} element={<Welcome />} />
        <Route path={ROUTES.COMMUNITY_INPUT.SEARCH} element={<Search />} />
        <Route path={ROUTES.COMMUNITY_INPUT.CONFIRM} element={<NavigateWithCurrentParams to={ROUTES.COMMUNITY_INPUT.SEARCH} />} />
        <Route path={ROUTES.COMMUNITY_INPUT.SUBMIT} element={<Submit />} />

        {/* EliseGO Configuration */}
        <Route path={ROUTES.ELISEGO.NAME} element={<EliseGOName />} />
        <Route path={ROUTES.ELISEGO.AI_NAME} element={<EliseGOAIName />} />
        <Route path={ROUTES.ELISEGO.VOICE_AREA_CODE} element={<EliseGOVoiceArea />} />
        <Route path={ROUTES.ELISEGO.SCHEDULE_TOURS} element={<EliseGOScheduleTours />} />
        <Route path={ROUTES.ELISEGO.SUBMIT} element={<EliseGOSubmit />} />

        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.NEXT_STEPS} element={<CharacteristicsWelcome />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.AFFORDABLE_INCOME} element={<CharacteristicsIncome />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.SECTION_8} element={<CharacteristicsSection8 />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.NEW_CONSTRUCTION} element={<CharacteristicsNewConstruction />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.STUDENT} element={<CharacteristicsStudent />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.SHARED_OPERATIONS} element={<CharacteristicsSharedOps />} />
        <Route path={ROUTES.COMMUNITY_CHARACTERISTICS.SUBMIT_FOR_REVIEW} element={<SubmitForReview />} />

        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.NEXT_STEPS} element={<ImplementationContactsWelcome />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.PROJECT_LEAD} element={<ImplementationProjectLead roleKey={'project_lead_team_member' as any} title="Implementation Contacts - Project Lead" question="Who will oversee the onboarding and launch of your properties?" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.TECHNICAL} backPath={ROUTES.IMPLEMENTATION_CONTACTS.NEXT_STEPS} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.TECHNICAL} element={<ImplementationTechnical roleKey={'technical_team_member' as any} title="Implementation Contacts - Integrations / Technical" question="Who is responsible for managing integrations with PMS / CRM systems?" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.VOICE} backPath={ROUTES.IMPLEMENTATION_CONTACTS.PROJECT_LEAD} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.VOICE} element={<ImplementationVoice roleKey={'voice_team_member' as any} title="Implementation Contacts - Voice" question="Who is knowledgable about your voice / phone tree setup?" subtitle="Skip if not relevant to your implementation" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.MARKETING} backPath={ROUTES.IMPLEMENTATION_CONTACTS.TECHNICAL} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.MARKETING} element={<ImplementationMarketing roleKey={'marketing_team_member' as any} title="Implementation Contacts - Marketing Team Members" question="Who from your marketing team will be responsible for leasing product setup and ILS traffic?" subtitle="Skip if not relevant to your implementation" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.KNOWLEDGE} backPath={ROUTES.IMPLEMENTATION_CONTACTS.VOICE} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.SUBMIT} element={<ImplementationContactsSubmit />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.KNOWLEDGE} element={<ImplementationKnowledge roleKey={'knowledge_team_member' as any} title="Implementation Contacts - Community Knowledge" question="Who has the best knowledge about the features of your communities that tenants and residents will commonly ask about?" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.TRAINING} backPath={ROUTES.IMPLEMENTATION_CONTACTS.MARKETING} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.TRAINING} element={<ImplementationTraining roleKey={'training_team_member' as any} title="Implementation Contacts - L&D / Training" question="Who will be overseeing or coordinating learning and development and/or training on EliseAI products?" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.OWNER} backPath={ROUTES.IMPLEMENTATION_CONTACTS.KNOWLEDGE} />} />
        <Route path={ROUTES.IMPLEMENTATION_CONTACTS.OWNER} element={<ImplementationOwner roleKey={'owner_team_member' as any} title="Implementation Contacts - Ownership" question="Who in the ownership group(s) would be interested in receiving reporting access?" nextPath={ROUTES.IMPLEMENTATION_CONTACTS.SUBMIT} backPath={ROUTES.IMPLEMENTATION_CONTACTS.TRAINING} />} />
      </Route>
      <Route path="/" element={<RootRedirect />} />
      
      {/* 404 */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};
